import { Button, Flex, HStack, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import ParkingSpaceInput from "../common/PopUp/ChakraFormik/ParkingSpaceInput";
import FormikInput from "../common/PopUp/ChakraFormik/FormikInput";
import { useFormik } from "formik";
import { isoToInputDate } from "../../functions/formatTime";
import { fetchWithToken } from "../../functions/fetch";
import { useQueryClient } from "@tanstack/react-query";
import uniqid from "uniqid";
import { editLeaseSchema } from "../../validation/leaseValidation";
import color from "../../config/color";
import DateRange from "../common/PopUp/ChakraFormik/DateRange";
import FormikSelect from "../common/PopUp/ChakraFormik/FormikSelect";
import ErrorMessages from "../common/PopUp/ChakraFormik/ErrorMessages";
import { carFeeType, scooterFeeType } from "../../config/config";
import FormikTextarea from "../common/PopUp/ChakraFormik/FormikTextarea";

const EditLease = ({
  lease,
  setSubmitFunction,
  pageIndex,
  setIsSubmitting,
  renewLease,
}) => {
  const [carParkingSpaces, setCarParkingSpaces] = useState([]);
  const [scooterParkingSpaces, setScooterParkingSpaces] = useState([]);
  const [firstRenderScooterInput, setFirstRenderScooterInput] = useState(true);
  const [firstRenderCarInput, setFirstRenderCarInput] = useState(true);
  const [errorMessages, setErrorMessages] = useState([]);
  const [rooms, setRooms] = useState([]);

  const queryClient = useQueryClient();

  const getParkingLeases = (type) => {
    return lease.parkingLeases.filter(
      (lease) => lease.parkingSpace.type === type,
    );
  };

  const carParkingLease = getParkingLeases("汽車")[0];
  const scooterParkingLeases = getParkingLeases("機車");
  const scooterParkingLease =
    scooterParkingLeases.length >= 1 ? scooterParkingLeases[0] : null;
  const scooterParkingLease2 =
    scooterParkingLeases.length >= 2 ? scooterParkingLeases[1] : null;

  // type: CAR|SCOOTER
  const fetchRentableParkingSpace = async (startDate, endDate, type) => {
    // get ignoreLeaseId
    const ignoreLeaseId =
      type === "SCOOTER"
        ? scooterParkingLease?._id
        : type === "CAR"
          ? carParkingLease?._id
          : "";

    const ignoreLeaseId2 = type === "SCOOTER" ? scooterParkingLease2?._id : "";

    const res = await fetchWithToken(
      "GET",
      `${process.env.REACT_APP_BACKEND_HOST}/api/parking-space/rentable?startDate=${startDate}&endDate=${endDate}&ignoreLeaseId=${ignoreLeaseId}&ignoreLeaseId2=${ignoreLeaseId2}`,
    );
    if (res.status === 200) {
      const { parkingSpaces } = res;
      parkingSpaces.sort((a, b) => a.number - b.number);
      if (type === "SCOOTER") {
        setScooterParkingSpaces(
          parkingSpaces.filter((ps) => ps.type === "機車"),
        );
      } else if (type === "CAR") {
        setCarParkingSpaces(parkingSpaces.filter((ps) => ps.type === "汽車")); // sort by room number
      } else {
        console.warn("Invalid type for fetching rentable parking space");
      }
    } else {
      console.error(res);
    }
  };

  const fetchRentableRooms = async (startDate, endDate) => {
    const res = await fetchWithToken(
      "GET",
      `${process.env.REACT_APP_BACKEND_HOST}/api/room/rentable?startDate=${startDate}&endDate=${endDate}&ignoreLeaseId=${lease._id}`,
    );
    if (res.status === 200) {
      const { rooms } = res;
      // sort by room number
      setRooms(
        rooms.sort((a, b) => {
          const roomNum1 = a.roomNumber.replace(/F/g, "4");
          const roomNum2 = b.roomNumber.replace(/F/g, "4");
          return roomNum1.localeCompare(roomNum2);
        }),
      );
    } else {
      console.error(res);
    }
  };

  const formik = useFormik({
    initialValues: {
      // SECTION: rent
      rentStartDate: isoToInputDate(lease.startDate),
      rentEndDate: isoToInputDate(lease.endDate),
      // The above two fields are for validation
      note: lease?.note,
      rentCost: lease.room.cost,
      carFee: lease.lastCarFee ?? "0",
      roomId: lease.room._id,
      // SECTION: scooter
      haveScooterParkingSpace:
        scooterParkingLease !== undefined && scooterParkingLease !== null,
      scooterLeaseStartDate:
        isoToInputDate(scooterParkingLease?.startDate) ?? "",
      scooterLeaseEndDate: isoToInputDate(scooterParkingLease?.endDate) ?? "",
      scooterParkingSpaceId: scooterParkingLease?.parkingSpace._id,
      scooterFee: lease.lastScooterFee ?? "0", // existing scooter fee won't be affected, so using 0 is fine
      scooterLeaseId: scooterParkingLease?._id,
      // SECTION: scooter2
      haveScooterParkingSpace2:
        scooterParkingLease2 !== undefined && scooterParkingLease2 !== null,
      scooterLeaseStartDate2:
        isoToInputDate(scooterParkingLease2?.startDate) ?? "",
      scooterLeaseEndDate2: isoToInputDate(scooterParkingLease2?.endDate) ?? "",
      scooterParkingSpaceId2: scooterParkingLease2?.parkingSpace._id,
      scooterFee2: lease.lastScooterFee2 ?? "0", // existing scooter fee won't be affected, so using 0 is fine
      scooterLeaseId2: scooterParkingLease2?._id,
      // SECTION: car
      haveCarParkingSpace:
        carParkingLease !== undefined && carParkingLease !== null,
      carLeaseStartDate: isoToInputDate(carParkingLease?.startDate) ?? "",
      carLeaseEndDate: isoToInputDate(carParkingLease?.endDate) ?? "",
      carParkingSpaceId: carParkingLease?.parkingSpace._id,
      carLeaseId: carParkingLease?._id,
    },
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const res = await fetchWithToken(
          "PUT",
          `${process.env.REACT_APP_BACKEND_HOST}/api/lease/${lease._id}`,
          values,
        );
        if (res.status === 200) {
          // lease and fee might change
          queryClient.invalidateQueries({ queryKey: ["fees", lease._id] });
          queryClient.invalidateQueries({ queryKey: ["leases"] });
        } else {
          const { chineseErrorMessage } = res;
          if (chineseErrorMessage) {
            setErrorMessages([chineseErrorMessage]);
          }
          console.error(res);
        }
      } catch (err) {
        console.error(err);
      }
      setIsSubmitting(false);
    },
    validationSchema: editLeaseSchema,
  });

  // when dates are changed, clear room field, and refetch rooms
  useEffect(() => {
    const { rentStartDate, rentEndDate } = formik.values;
    // both values exist
    if (
      rentStartDate?.length > 0 &&
      rentEndDate?.length > 0 &&
      new Date(rentStartDate) <= new Date(rentEndDate)
    ) {
      // when using original date, use original room
      if (
        rentStartDate === isoToInputDate(lease.startDate) &&
        rentEndDate === isoToInputDate(lease.endDate)
      ) {
        formik.setFieldValue("roomId", lease.room._id);
      } else {
        formik.setFieldValue("roomId", ""); // clear the room
      }
      fetchRentableRooms(rentStartDate, rentEndDate); // fetch with updated value
    }
  }, [formik.values.rentStartDate, formik.values.rentEndDate]);

  // when dates for parkingSpaces are changed, clear parking space field, and refetch them
  // car parking space
  useEffect(() => {
    const { carLeaseStartDate, carLeaseEndDate, haveCarParkingSpace } =
      formik.values;
    // need refetch car parking space
    if (
      haveCarParkingSpace &&
      carLeaseStartDate?.length > 0 &&
      carLeaseEndDate?.length > 0 &&
      new Date(carLeaseStartDate) <= new Date(carLeaseEndDate)
    ) {
      // use the default value when first rendering
      if (!firstRenderCarInput) {
        formik.setFieldValue("carParkingSpaceId", ""); // clear the parking space
      }
      setFirstRenderCarInput(false);
      fetchRentableParkingSpace(carLeaseStartDate, carLeaseEndDate, "CAR"); // refetch
    }
  }, [
    formik.values.carLeaseStartDate,
    formik.values.carLeaseEndDate,
    formik.values.haveCarParkingSpace,
  ]);

  // scooter parking space
  useEffect(() => {
    const {
      scooterLeaseStartDate,
      scooterLeaseEndDate,
      haveScooterParkingSpace,
    } = formik.values;
    // need refetch scooter parking space
    if (
      haveScooterParkingSpace &&
      scooterLeaseStartDate?.length > 0 &&
      scooterLeaseEndDate?.length > 0 &&
      new Date(scooterLeaseStartDate) <= new Date(scooterLeaseEndDate)
    ) {
      if (!firstRenderScooterInput) {
        formik.setFieldValue("scooterParkingSpaceId", ""); // clear the parking space
      }
      fetchRentableParkingSpace(
        scooterLeaseStartDate,
        scooterLeaseEndDate,
        "SCOOTER",
      ); // refetch
      setFirstRenderScooterInput(false);
    }
  }, [
    formik.values.scooterLeaseStartDate,
    formik.values.scooterLeaseEndDate,
    formik.values.haveScooterParkingSpace,
  ]);

  // set submit function after switching page
  useEffect(() => {
    if (pageIndex === 0) {
      setSubmitFunction(() => formik.handleSubmit);
    }
  }, [pageIndex]);

  // when room number changed, use cost of room as default cost
  useEffect(() => {
    const { roomId } = formik.values;
    const currentRoom = rooms.find((room) => room._id === roomId);
    formik.setFieldValue("rentCost", currentRoom?.cost ?? "");
  }, [formik.values.roomId, rooms]);

  return (
    <Flex direction="column" gap={2}>
      <HStack>
        <DateRange
          formik={formik}
          field1="rentStartDate"
          field2="rentEndDate"
          isRequired
        />
        <Button
          colorScheme="orange"
          bgColor={color.orange}
          size="sm"
          onClick={() => renewLease(lease)}
        >
          續約
        </Button>
      </HStack>
      <HStack>
        <FormikSelect
          field="roomId"
          formik={formik}
          labelText="房號："
          isRequired
        >
          <option value="">無</option>
          {rooms.map((room) => (
            <option key={uniqid()} value={room._id}>
              {room.roomNumber}
            </option>
          ))}
        </FormikSelect>
        <FormikInput
          formik={formik}
          labelText="房租："
          field="rentCost"
          isRequired
        />
      </HStack>
      {lease.renters.map((renter) => (
        <Flex gap={2} key={uniqid()}>
          <Text>{`住戶身分證：${renter.idNumber ?? ""}`}</Text>
          <Text>{`住戶姓名：${renter.name ?? ""}`}</Text>
          <Text>{`聯絡電話：${renter.phoneNumber ?? ""}`}</Text>
        </Flex>
      ))}
      <ParkingSpaceInput
        formik={formik}
        haveSpaceField="haveScooterParkingSpace"
        labelText="機車位編號"
        startDateField="scooterLeaseStartDate"
        idField="scooterParkingSpaceId"
        endDateField="scooterLeaseEndDate"
        feeLabelText={scooterFeeType}
        feeField="scooterFee"
        dateLabel="機車租期："
      >
        <option value="">無</option>
        {scooterParkingSpaces.map((ps) => (
          <option key={uniqid()} value={ps._id}>
            {ps.number}
          </option>
        ))}
      </ParkingSpaceInput>
      <ParkingSpaceInput
        formik={formik}
        haveSpaceField="haveScooterParkingSpace2"
        labelText="機車位編號"
        startDateField="scooterLeaseStartDate2"
        idField="scooterParkingSpaceId2"
        endDateField="scooterLeaseEndDate2"
        feeLabelText={scooterFeeType}
        feeField="scooterFee2"
        dateLabel="機車租期："
      >
        <option value="">無</option>
        {scooterParkingSpaces.map((ps) => (
          <option key={uniqid()} value={ps._id}>
            {ps.number}
          </option>
        ))}
      </ParkingSpaceInput>
      <ParkingSpaceInput
        labelText="汽車位編號"
        formik={formik}
        idField="carParkingSpaceId"
        startDateField="carLeaseStartDate"
        endDateField="carLeaseEndDate"
        haveSpaceField="haveCarParkingSpace"
        feeLabelText={carFeeType}
        feeField="carFee"
        dateLabel="汽車租期："
      >
        <option value="">無</option>
        {carParkingSpaces.map((ps) => (
          <option key={uniqid()} value={ps._id}>
            {ps.number}
          </option>
        ))}
      </ParkingSpaceInput>
      <FormikTextarea formik={formik} field="note" labelText="備註：" />
      <ErrorMessages formik={formik} />
      {errorMessages.map((text) => (
        <Text color="red.500" whiteSpace="pre" key={uniqid()}>
          {text}
        </Text>
      ))}
    </Flex>
  );
};

export default EditLease;
